import React from 'react';
import { 
    FlexDiv, 
    Block,
    Heading4,
    TextDefault,
    TextInput
} from '@awarego-ui/components';
import { Checkbox, FormControl, MenuItem, Select, InputLabel, TextField } from '@mui/material';

function PropertiesPanel({ 
    selectedBlock, 
    selectedPresentation, 
    question,
    onUpdateBlock, 
    onUpdatePresentation, 
    onUpdateQuestion 
}) {
    const renderBlockProperties = () => {
        if (!selectedBlock) return null;

        return (
            <Block padding={16} gap={16}>
                <Heading4>Block Properties</Heading4>
                
                <FlexDiv column gap={12}>
                    <TextDefault bold>Type: {selectedBlock.type}</TextDefault>
                    
                    <TextInput
                        label="Title"
                        value={selectedBlock.title || ''}
                        onChange={(e) => onUpdateBlock({ title: e.target.value })}
                        placeholder="Block title"
                    />

                    {selectedBlock.type === 'radio' || selectedBlock.type === 'checkbox' ? (
                        <FlexDiv column gap={8}>
                            <TextDefault bold>Options:</TextDefault>
                            {selectedBlock.options?.map((option, index) => (
                                <FlexDiv key={index} alignCenter gap={8}>
                                    <TextInput
                                        value={option}
                                        onChange={(e) => {
                                            const newOptions = [...selectedBlock.options];
                                            newOptions[index] = e.target.value;
                                            onUpdateBlock({ options: newOptions });
                                        }}
                                        placeholder="Option text"
                                    />
                                </FlexDiv>
                            ))}
                        </FlexDiv>
                    ) : null}

                    {selectedBlock.type === 'numericalscale' ? (
                        <FlexDiv column gap={8}>
                            <FlexDiv alignCenter gap={12}>
                                <TextInput
                                    label="Min Value"
                                    type="number"
                                    value={selectedBlock.min || 1}
                                    onChange={(e) => onUpdateBlock({ min: parseInt(e.target.value) })}
                                />
                                <TextInput
                                    label="Max Value"
                                    type="number"
                                    value={selectedBlock.max || 10}
                                    onChange={(e) => onUpdateBlock({ max: parseInt(e.target.value) })}
                                />
                            </FlexDiv>
                        </FlexDiv>
                    ) : null}

                    {selectedBlock.type === 'yes-no-list' ? (
                        <FlexDiv column gap={8}>
                            <TextDefault bold>Questions:</TextDefault>
                            {selectedBlock.questions?.map((q, index) => (
                                <FlexDiv key={index} column gap={8}>
                                    <TextInput
                                        value={q.text}
                                        onChange={(e) => {
                                            const newQuestions = [...selectedBlock.questions];
                                            newQuestions[index] = { ...q, text: e.target.value };
                                            onUpdateBlock({ questions: newQuestions });
                                        }}
                                        placeholder="Question text"
                                    />
                                    <FlexDiv alignCenter gap={8}>
                                        <Checkbox
                                            checked={q.required || false}
                                            onChange={(e) => {
                                                const newQuestions = [...selectedBlock.questions];
                                                newQuestions[index] = { ...q, required: e.target.checked };
                                                onUpdateBlock({ questions: newQuestions });
                                            }}
                                        />
                                        <TextDefault>Required</TextDefault>
                                    </FlexDiv>
                                </FlexDiv>
                            ))}
                        </FlexDiv>
                    ) : null}

                    {selectedBlock.type === 'orderablelist' ? (
                        <FlexDiv column gap={8}>
                            <TextDefault bold>Items:</TextDefault>
                            {selectedBlock.items?.map((item, index) => (
                                <TextInput
                                    key={index}
                                    value={item}
                                    onChange={(e) => {
                                        const newItems = [...selectedBlock.items];
                                        newItems[index] = e.target.value;
                                        onUpdateBlock({ items: newItems });
                                    }}
                                    placeholder="Item text"
                                />
                            ))}
                        </FlexDiv>
                    ) : null}

                    {selectedBlock.type === 'conversation' ? (
                        <FlexDiv column gap={8}>
                            <TextDefault bold>Messages:</TextDefault>
                            {selectedBlock.messages?.map((message, index) => (
                                <FlexDiv key={index} column gap={8}>
                                    <FormControl fullWidth>
                                        <InputLabel>Sender</InputLabel>
                                        <Select
                                            value={message.sender}
                                            onChange={(e) => {
                                                const newMessages = [...selectedBlock.messages];
                                                newMessages[index] = { ...message, sender: e.target.value };
                                                onUpdateBlock({ messages: newMessages });
                                            }}
                                        >
                                            <MenuItem value="User">User</MenuItem>
                                            <MenuItem value="Bot">Bot</MenuItem>
                                            <MenuItem value="System">System</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        multiline
                                        rows={3}
                                        fullWidth
                                        value={message.text}
                                        onChange={(e) => {
                                            const newMessages = [...selectedBlock.messages];
                                            newMessages[index] = { ...message, text: e.target.value };
                                            onUpdateBlock({ messages: newMessages });
                                        }}
                                        placeholder="Message text"
                                    />
                                </FlexDiv>
                            ))}
                        </FlexDiv>
                    ) : null}

                    {selectedBlock.type === 'questionwithbuttons' ? (
                        <FlexDiv column gap={8}>
                            <TextDefault bold>Buttons:</TextDefault>
                            {selectedBlock.buttons?.map((button, index) => (
                                <TextInput
                                    key={index}
                                    value={button}
                                    onChange={(e) => {
                                        const newButtons = [...selectedBlock.buttons];
                                        newButtons[index] = e.target.value;
                                        onUpdateBlock({ buttons: newButtons });
                                    }}
                                    placeholder="Button text"
                                />
                            ))}
                        </FlexDiv>
                    ) : null}

                    {selectedBlock.type === 'questionwithimage' ? (
                        <FlexDiv column gap={8}>
                            <TextInput
                                label="Image URL"
                                value={selectedBlock.imageUrl || ''}
                                onChange={(e) => onUpdateBlock({ imageUrl: e.target.value })}
                                placeholder="https://example.com/image.jpg"
                            />
                        </FlexDiv>
                    ) : null}

                    <FlexDiv alignCenter gap={8}>
                        <Checkbox
                            checked={selectedBlock.required || false}
                            onChange={(e) => onUpdateBlock({ required: e.target.checked })}
                        />
                        <TextDefault>Required</TextDefault>
                    </FlexDiv>
                </FlexDiv>
            </Block>
        );
    };

    const renderPresentationProperties = () => {
        if (!selectedPresentation) return null;

        return (
            <Block padding={16} gap={16}>
                <Heading4>Presentation Properties</Heading4>
                
                <FlexDiv column gap={12}>
                    <TextDefault bold>Type: {selectedPresentation.type}</TextDefault>
                    
                    <TextInput
                        label="Title"
                        value={selectedPresentation.title || ''}
                        onChange={(e) => onUpdatePresentation({ title: e.target.value })}
                        placeholder="Presentation title"
                    />

                    {selectedPresentation.type === 'email' ? (
                        <FlexDiv column gap={8}>
                            <TextInput
                                label="Subject"
                                value={selectedPresentation.subject || ''}
                                onChange={(e) => onUpdatePresentation({ subject: e.target.value })}
                                placeholder="Email subject"
                            />
                            <TextInput
                                label="Sender"
                                value={selectedPresentation.sender || ''}
                                onChange={(e) => onUpdatePresentation({ sender: e.target.value })}
                                placeholder="sender@example.com"
                            />
                            <TextField
                                multiline
                                rows={4}
                                fullWidth
                                label="Content"
                                value={selectedPresentation.content || ''}
                                onChange={(e) => onUpdatePresentation({ content: e.target.value })}
                                placeholder="Email content"
                            />
                        </FlexDiv>
                    ) : null}

                    {selectedPresentation.type === 'message' || selectedPresentation.type === 'password' || selectedPresentation.type === 'question' ? (
                        <TextField
                            multiline
                            rows={4}
                            fullWidth
                            label="Content"
                            value={selectedPresentation.content || ''}
                            onChange={(e) => onUpdatePresentation({ content: e.target.value })}
                            placeholder="Content text"
                        />
                    ) : null}

                    {selectedPresentation.type === 'illustration' || selectedPresentation.type === 'interactive-img' ? (
                        <TextInput
                            label="Image URL"
                            value={selectedPresentation.imageUrl || ''}
                            onChange={(e) => onUpdatePresentation({ imageUrl: e.target.value })}
                            placeholder="https://example.com/image.jpg"
                        />
                    ) : null}

                    {selectedPresentation.type === 'video' ? (
                        <FlexDiv column gap={8}>
                            <TextInput
                                label="Video URL"
                                value={selectedPresentation.videoUrl || ''}
                                onChange={(e) => onUpdatePresentation({ videoUrl: e.target.value })}
                                placeholder="https://example.com/video.mp4"
                            />
                            <FlexDiv alignCenter gap={8}>
                                <Checkbox
                                    checked={selectedPresentation.autoplay || false}
                                    onChange={(e) => onUpdatePresentation({ autoplay: e.target.checked })}
                                />
                                <TextDefault>Autoplay</TextDefault>
                            </FlexDiv>
                        </FlexDiv>
                    ) : null}
                </FlexDiv>
            </Block>
        );
    };

    const renderQuestionProperties = () => {
        return (
            <Block padding={16} gap={16}>
                <Heading4>Question Properties</Heading4>
                
                <FlexDiv column gap={12}>
                    <TextInput
                        label="Question Name"
                        value={question.name || ''}
                        onChange={(e) => onUpdateQuestion({ name: e.target.value })}
                        placeholder="Question name"
                    />
                    
                    <TextField
                        multiline
                        rows={3}
                        fullWidth
                        label="Description"
                        value={question.description || ''}
                        onChange={(e) => onUpdateQuestion({ description: e.target.value })}
                        placeholder="Question description"
                    />
                    
                    <FormControl fullWidth>
                        <InputLabel>Threat Area</InputLabel>
                        <Select
                            value={question.area || 'General'}
                            onChange={(e) => onUpdateQuestion({ area: e.target.value })}
                        >
                            <MenuItem value="General">General</MenuItem>
                            <MenuItem value="Phishing">Phishing</MenuItem>
                            <MenuItem value="Social Engineering">Social Engineering</MenuItem>
                            <MenuItem value="Password Security">Password Security</MenuItem>
                            <MenuItem value="Data Protection">Data Protection</MenuItem>
                        </Select>
                    </FormControl>
                    
                    <FormControl fullWidth>
                        <InputLabel>Difficulty</InputLabel>
                        <Select
                            value={question.difficulty || 'basic'}
                            onChange={(e) => onUpdateQuestion({ difficulty: e.target.value })}
                        >
                            <MenuItem value="basic">Basic</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="advanced">Advanced</MenuItem>
                        </Select>
                    </FormControl>
                </FlexDiv>
            </Block>
        );
    };

    return (
        <div className="properties-panel">
            <FlexDiv column gap={16}>
                {renderQuestionProperties()}
                {renderBlockProperties()}
                {renderPresentationProperties()}
                
                {!selectedBlock && !selectedPresentation && (
                    <Block padding={16}>
                        <TextDefault lighter>
                            Select a block or presentation to edit its properties
                        </TextDefault>
                    </Block>
                )}
            </FlexDiv>
        </div>
    );
}

export default PropertiesPanel;
